import ZionForm, { FormRadio, FormRadioGroup, useZionForm } from '@fs/zion-form'
import {
  getNestedOrFloatingElementById,
  getPersonName,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { Button, DataBlock, InfoSheetPanel, Row, Separator, useOverlay } from '@fs/zion-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { object, string } from 'yup'
import { useFeatureFlag } from '@fs/zion-flags'
import { useR2DispatchContext, useR2StateContext } from '../../../../../providers/r2Provider/R2Context'
import PanelHeader from '../../../../../components/generic/panels/shared/PanelHeader'
import UnsavedChangesOverlay from '../../../../../components/generic/panels/shared/UnsavedChangesOverlay'
import FieldWrapper from '../../../../../components/generic/panels/uiHelpers/FieldWrapper'
import RowWrapper from '../../../../../components/generic/panels/uiHelpers/RowWrapper'
import useUnsavedChangesOverlay from '../../../../../hooks/useUnsavedChangesOverlay'
import { useOverlayContext } from '../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { getHighlightsForField, createFieldInfoForSelectList, getPluralizationText } from './SharedHighlightUtils'

const schema = object().shape({
  selectedFieldRadio: string().required(),
})

const SelectSlsField = ({ fieldValue, setFieldValue, saving = false, onDiscard }) => {
  const [t] = useTranslation()
  const r2Dispatch = useR2DispatchContext()
  const { editSharedHighlightPage1Overlay, openOverlay, closeOverlay } = useOverlayContext()
  const { activeFieldInfo, getHighlights } = useSlsHighlightsContext()
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const unsavedChangesOverlay = useOverlay()
  const saveButtonRef = useRef(null)
  const reassignHighlightFlow = useFeatureFlag('rip_sharedImageViewer_reassignHighlightFlow')

  const newFieldForHighlight = activeFieldInfo

  const noDataLabel = t('review-highlight-linked-fields.new-field.no-data', 'No data')

  const formMethods = useZionForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      selectedFieldRadio: fieldValue,
    },
  })

  useUnsavedChangesOverlay({ methods: formMethods, overlay: unsavedChangesOverlay, forceWarning: true })

  /**
   * A memoized array of highlights on the current image for the currently selected field
   */
  const { highlightsOnSelectedField, splitScreenHighlightsOnSelectedField } = useMemo(() => {
    const { mainEditHighlights, splitScreenEditHighlights } = getHighlights()

    return {
      highlightsOnSelectedField: getHighlightsForField(
        state.currentImageArk,
        mainEditHighlights,
        r2State.selectedPersonId,
        activeFieldInfo.fieldType
      ),
      splitScreenHighlightsOnSelectedField: getHighlightsForField(
        thumbnails?.[r2State.referenceFocusedIndex]?.ark,
        splitScreenEditHighlights,
        r2State.selectedPersonId,
        activeFieldInfo.fieldType
      ),
    }
  }, [
    state.currentImageArk,
    r2State.referenceFocusedIndex,
    thumbnails,
    getHighlights,
    r2State.selectedPersonId,
    activeFieldInfo.fieldType,
  ])

  /**
   * A memoized list of the information we need from the fields associated with the highlights
   * To be used in populating the Radio elements below
   */
  const fieldInfoForSelectList = useMemo(
    () =>
      createFieldInfoForSelectList({
        highlightsOnSelectedField,
        splitScreenHighlightsOnSelectedField,
        newFieldForHighlight,
        images: state.images,
        fieldTypes: state.types?.fields,
        state,
      }),
    [highlightsOnSelectedField, splitScreenHighlightsOnSelectedField, newFieldForHighlight, state]
  )

  const { header: pluralizedSelectFieldInstructionsHeader, instructions: pluralizesSelectFieldInstructions } =
    getPluralizationText({
      fieldInfoForSelectList,
      newFieldForHighlight,
      isReassignFlow: reassignHighlightFlow.isOn,
      t,
    })

  const handleClose = useCallback(() => {
    r2Dispatch({
      type: 'SET_EDIT_DISPATCHES',
      value: [],
    })
    closeOverlay('editSharedHighlightPage1')
    formMethods.reset()
  }, [closeOverlay, formMethods, r2Dispatch])

  useUnsavedChangesOverlay({
    methods: formMethods,
    overlay: unsavedChangesOverlay,
    panelId: 'editSharedHighlightPage1',
  })

  // Get person name for dynamic title when flag is on
  const selectedPersonId = r2State.selectedPersonId || r2State.createdPersonId
  const selectedPerson = getNestedOrFloatingElementById(state.images, selectedPersonId)
  const personName = selectedPerson ? getPersonName({ person: selectedPerson, justOneName: true }) : ''

  // Compute panel title
  const panelTitle = useMemo(() => {
    if (reassignHighlightFlow.isOn && personName) {
      return t('review-highlight-linked-fields.infosheet.title-reassign', 'Edit {personName}', { personName })
    }
    return t('review-highlight-linked-fields.infosheet.title', 'Add Linked Field')
  }, [reassignHighlightFlow.isOn, personName, t])

  // Compute panel heading
  const panelHeading = useMemo(() => {
    if (reassignHighlightFlow.isOn) {
      return t('review-highlight-linked-fields.panel-header.heading-reassign', 'Reassign the Highlight')
    }
    return t('review-highlight-linked-fields.panel-header.heading', 'Review Linked Fields')
  }, [reassignHighlightFlow.isOn, t])

  // Compute panel caption
  const panelCaption = useMemo(() => {
    if (reassignHighlightFlow.isOn) {
      return t(
        'review-highlight-linked-fields.panel-header.caption-reassign',
        'The highlight is about to move from the assigned field to another.'
      )
    }
    return t('review-highlight-linked-fields.panel-header.caption', 'Creating linked fields via highlights')
  }, [reassignHighlightFlow.isOn, t])

  return (
    <InfoSheetPanel
      title={panelTitle}
      headingColor="yellow"
      {...{ ...editSharedHighlightPage1Overlay, close: handleClose }}
      footer={
        <Row alignX="end" gutters="md">
          <Button
            data-testid="panel-back-button"
            emphasis="low"
            type="submit"
            form=""
            onClick={() => {
              handleClose()
            }}
            disabled={saving}
          >
            {t('navigation.back-button', 'Back')}
          </Button>
          <Button
            emphasis="high"
            onClick={() => openOverlay('editSharedHighlightPage2')}
            disabled={!formMethods.formState.isValid}
            ref={saveButtonRef}
          >
            {t('navigation.next-button', 'Next')}
          </Button>
        </Row>
      }
    >
      <ZionForm methods={formMethods} id="review-highlight-linked-fields">
        <PanelHeader heading={panelHeading} caption={panelCaption} headingSize="H5" />
        <Separator size="xs" />
        <RowWrapper>
          <DataBlock heading={pluralizedSelectFieldInstructionsHeader} data={pluralizesSelectFieldInstructions} />
        </RowWrapper>
        <RowWrapper>
          <FieldWrapper>
            <FormRadioGroup orientation="vertical">
              {fieldInfoForSelectList
                .filter((field) => !!field && !!field.label)
                .map((field) => {
                  const value =
                    typeof field.value === 'object'
                      ? field.value.standard?.standardizedText || field.value.originalText
                      : field.value
                  return (
                    <FormRadio
                      data-testid="current-highlight-field-radio"
                      key={field.id}
                      label={`${field.label}: ${value || noDataLabel}`}
                      name="selectedFieldRadio"
                      value={field.id}
                      onClick={() => {
                        setFieldValue(field.value)
                      }}
                    />
                  )
                })}
            </FormRadioGroup>
          </FieldWrapper>
        </RowWrapper>
      </ZionForm>
      <UnsavedChangesOverlay
        overlay={unsavedChangesOverlay}
        onDiscard={onDiscard}
        onSave={() => saveButtonRef.current.click()}
        formId="review-highlight-linked-fields"
        alertMode
      />
    </InfoSheetPanel>
  )
}

export default SelectSlsField
